import { useEffect, useRef, useState } from "react";

export interface CoverVideoProps {
  src: string;
  /** The cover's first frame, shown until it plays; none leaves the card's own fill. */
  poster: string | null;
  /** Both layers', each filling the card. */
  className?: string | undefined;
  /** The cover can be shown: its poster has loaded (with none, the video's size is known), or failed. */
  onReady?: () => void;
}

/**
 * Live's animated cover (`PreloadedMedia`: autoplaying, looped, inline, muted), with what it
 * fetches cut to what is seen (#828 bug 17: live's way loads every cover whole, 19 of them about
 * 28 MB on the templates page's first screen). Its first frame shows at once as a poster, under
 * the video; the video loads and plays only while it is on screen (`preload="none"` until then),
 * and pauses off screen and while the tab is hidden (live's `useAutoplayWhileVisible`).
 */
export function CoverVideo({ src, poster, className, onReady }: CoverVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (node === null) return;
    const observer = new IntersectionObserver((entries) => {
      setOnScreen(entries.some((entry) => entry.isIntersecting));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (node === null || !onScreen) return;
    node.preload = "auto";
    // A refusal leaves the poster, and nothing a browser's `play()` does ever unmounts the
    // gallery: refused (a promise that rejects), no promise (an older browser's, jsdom's), or a
    // throw.
    const play = () => {
      try {
        const played = node.play() as Promise<void> | undefined;
        void played?.catch(() => undefined);
      } catch {
        // The poster stays.
      }
    };
    const onVisibility = () => (document.hidden ? node.pause() : play());
    if (!document.hidden) play();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      node.pause();
    };
  }, [onScreen]);
  const clip = (
    // A cover has no words to caption.
    // oxlint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={ref}
      className={className}
      src={src}
      loop
      playsInline
      muted
      preload="none"
      aria-hidden
      onLoadedMetadata={poster === null ? onReady : undefined}
      onError={poster === null ? onReady : undefined}
    />
  );
  return (
    <>
      {poster === null ? null : (
        <img className={className} src={poster} alt="" draggable={false} onLoad={onReady} onError={onReady} />
      )}
      {clip}
    </>
  );
}
